import { isExerciseAdministrationPath } from './utils/exerciseAdministrationPath';
import { selectMatchContext } from './utils/selectMatchContext';
import { isPersonalExerciseWorkspacePath } from './utils/personalExerciseWorkspace';
import ExerciseLibraryEntry from './components/training/ExerciseLibraryEntry';
import BrandLogo from './components/BrandLogo';
import RegistrationWaitingPage from './components/RegistrationWaitingPage';
import { registrationWaitingState, type RegistrationAccessResult } from './utils/registrationWaitingState';
import { getRegistrationAccessStatus } from './lib/registerApi';
import { startPortalActivityTracking } from './lib/portalActivity';
import { StatisticsRefreshNotifications } from './components/StatisticsRefreshNotice';
/*
 * src/App.tsx
 *
 * This file defines the main application shell, <App />.
 * It is responsible for:
 * - Initializing user authentication state and application-wide data (teams, etc.).
 * - Handling routing between different pages/views of the application.
 * - Rendering the main layout, including the header, navigation, and footer.
 * - Managing global state such as the currently selected team and active UI tab.
 * - Checking and displaying API connection status.
 */
import { lazy, Suspense, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Main application shell. Handles routing between pages and displays
// the navigation header with team selection.
import Login from './pages/Login';
import AccountDeletion from './pages/AccountDeletion';
// Kept in the offline shell because the public saved-exercise viewer needs it on a cold offline start.
import TrainingExerciseDetail from './pages/TrainingExerciseDetail';
import Sidebar from './components/Sidebar';
import MobileShellNavigation from './components/MobileShellNavigation';
import { useUserStore } from './store/userStore';
import { useMatchStore } from './store/matchStore';
import RouteLoadBoundary from './components/RouteLoadBoundary';

import Footer from './components/Footer';
import HighPriorityIssueModal from './components/HighPriorityIssueModal';
import { useI18n, Locale } from './i18n';
import IssueForm from './pages/IssueForm';
import { useIssueFormStore } from './store/issueFormStore';
import { AlertCircle, AlertTriangle, Loader2, Lightbulb, LogOut, Save, Settings } from 'lucide-react';
import { AppIcons } from './config/AppIcons';
import { Routes, Route, Navigate, useNavigate, useParams, Outlet, useLocation } from 'react-router-dom';
import { useClubDetailStore } from './store/clubDetailStore';
import { useHighPriorityIssueStore } from './store/highPriorityIssueStore';
import { isDevelopmentEnv } from './utils/appEnv';
import { getBackendVersionApi } from './lib/versionApi';
import { useChannelStream } from './hooks/useChannelStream';
import { ConnectionStatusBanner } from './components/ConnectionStatusBanner';
import type { StreamEvent } from './lib/sseClient';
import { useLivePresenceStore } from './store/livePresenceStore';
import { AUTH_STORAGE_KEY, AUTH_USER_STORAGE_KEY } from './lib/authApi';
import { isOpenHighPriorityIssue } from './utils/githubRoadmap';
import { canAccessTeamPlayerStatistics } from './utils/teamPermissions';
import { canAccessOwnClubManagement, canAccessClubDetail } from './utils/clubPermissions';
import { getSelectedMatchDestination } from './utils/selectedMatchRoute';
import {
  resolveLegacyTrainerPlanDestination,
  resolveLegacyTrainerViewerDestination,
} from './utils/legacyTrainingRoute';
import { getTrainerOnlyHome, resolveMobileWorkspace } from './utils/mobileWorkspace';

const TeamManagement = lazy(() => import('./pages/TeamManagement'));
const MembershipApproval = lazy(() => import('./pages/MembershipApproval'));
const TrainerDugout = lazy(() => import('./pages/TrainerDugout'));
const TeamIntake = lazy(() => import('./pages/TeamIntake'));
function LocalizedTeamsRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: location.pathname.replace(/^\/(nl|en)(?=\/teams(?:\/|$))/, ''), search: location.search }} replace />;
}

const TeamsManagement = lazy(() => import('./pages/TeamsManagement'));
const PlayersManagement = lazy(() => import('./pages/PlayersManagement'));
const PlayerEvaluations = lazy(() => import('./pages/PlayerEvaluations'));
const ClubsManagement = lazy(() => import('./pages/ClubsManagement'));
const ClubUsers = lazy(() => import('./pages/ClubUsers'));
const ClubDetails = lazy(() => import('./pages/ClubDetails'));
const ClubManagementOverview = lazy(() => import('./pages/ClubManagementOverview'));
const Hub = lazy(() => import('./pages/Hub'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const ContactManagement = lazy(() => import('./pages/ContactManagement'));
const LineupManagement = lazy(() => import('./pages/LineupManagement'));
const MatchDashboard = lazy(() => import('./pages/MatchDashboard'));
const MatchPlanning = lazy(() => import('./pages/MatchPlanning'));
const SubstitutionSchedule = lazy(() => import('./pages/SubstitutionSchedule'));
const PlayerSchedule = lazy(() => import('./pages/PlayerSchedule'));
const PlayerStatistics = lazy(() => import('./pages/PlayerStatistics'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const LiveMatches = lazy(() => import('./pages/LiveMatches'));
const AdminEmail = lazy(() => import('./pages/AdminEmail'));
const AdminDecryptValue = lazy(() => import('./pages/AdminDecryptValue'));
const AdminWebDav = lazy(() => import('./pages/AdminStorage').then(module => ({ default: module.AdminWebDav })));
const AdminDatabase = lazy(() => import('./pages/AdminStorage').then(module => ({ default: module.AdminDatabase })));
const MatchAnalysis = lazy(() => import('./pages/MatchAnalysis'));
const CuratedExerciseManagement = lazy(() => import('./pages/CuratedExerciseManagement'));
const TrainingExercises = lazy(() => import('./pages/TrainingExercises'));
const TeamEvaluationDistributions = lazy(() => import('./pages/TeamEvaluationDistributions'));
const TeamEvaluationDistributionForm = lazy(() => import('./pages/TeamEvaluationDistributionForm'));
const TrainingExercisePlans = lazy(() => import('./pages/TrainingExercisePlans'));
const TrainingSessions = lazy(() => import('./pages/TrainingSessions'));
const TrainingCalendarPage = lazy(() => import('./pages/TrainingCalendar'));
const AdminPushRegistrations = lazy(() => import('./pages/AdminPushRegistrations'));
const PushPage = lazy(() => import('./pages/PushPage'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const ExerciseLibraryEditor = lazy(() => import('./pages/ExerciseLibraryEditor'));
const DrillEditor = lazy(() => import('./drilleditor/DrillEditorPage').then(module => ({ default: module.DrillEditor })));
const TrainingPlanEditor = lazy(() => import('./pages/TrainingPlanEditor'));
const TrainingSessionEditor = lazy(() => import('./pages/TrainingSessionEditor'));
const FormationEditor = lazy(() => import('./pages/FormationEditor'));
const FormationsManagement = lazy(() => import('./pages/FormationsManagement'));
const EvaluationsManagement = lazy(() => import('./pages/EvaluationsManagement'));
const MatchTypesManagement = lazy(() => import('./pages/MatchTypesManagement'));
const SecurityCodesManagement = lazy(() => import('./pages/SecurityCodesManagement'));
const SeasonsManagement = lazy(() => import('./pages/SeasonsManagement'));

const LegacyMobileRouteRedirect = ({ to }: { to: string }) => {
  const legacyLocation = useLocation();
  return (
    <Navigate
      to={{ pathname: to, search: legacyLocation.search, hash: legacyLocation.hash }}
      state={legacyLocation.state}
      replace
    />
  );
};

const LegacyCoachTeamRedirect = () => {
  const legacyLocation = useLocation();
  const { teamId } = useParams<{ teamId: string }>();
  return (
    <Navigate
      to={{
        pathname: teamId ? `/team-management/${encodeURIComponent(teamId)}` : '/',
        search: legacyLocation.search,
        hash: legacyLocation.hash,
      }}
      state={legacyLocation.state}
      replace
    />
  );
};

const LegacyCoachMatchRedirect = ({ prepare = false }: { prepare?: boolean }) => {
  const legacyLocation = useLocation();
  const { matchId } = useParams<{ matchId: string }>();
  const search = new URLSearchParams(legacyLocation.search);
  if (prepare) search.set('prepare', '1');
  return (
    <Navigate
      to={{
        pathname: matchId ? `/match/${encodeURIComponent(matchId)}` : '/match',
        search: search.toString() ? `?${search.toString()}` : '',
        hash: legacyLocation.hash,
      }}
      state={legacyLocation.state}
      replace
    />
  );
};

const LegacyTrainerViewerRedirect = () => {
  const legacyLocation = useLocation();
  const destination = resolveLegacyTrainerViewerDestination(legacyLocation.search);
  return (
    <Navigate
      to={{ ...destination, hash: legacyLocation.hash }}
      state={legacyLocation.state}
      replace
    />
  );
};

const LegacyTrainerPlanRedirect = () => {
  const legacyLocation = useLocation();
  const { planId } = useParams<{ planId: string }>();
  const destination = resolveLegacyTrainerPlanDestination(planId, legacyLocation.search);
  return (
    <Navigate
      to={{ ...destination, hash: legacyLocation.hash }}
      state={legacyLocation.state}
      replace
    />
  );
};

const TeamManagementRoute = ({ statistics = false }: { statistics?: boolean }) => {
  const { teamId } = useParams<{ teamId: string }>();
  const teams = useUserStore(state => state.teams);
  const currentUser = useUserStore(state => state.currentUser);
  const currentTeamId = useUserStore(state => state.currentTeamId);
  const setCurrentTeam = useUserStore(state => state.setCurrentTeam);
  const requestedTeam = teamId ? teams.find(team => team.id === teamId) : null;

  useEffect(() => {
    if (requestedTeam && currentTeamId !== requestedTeam.id) {
      setCurrentTeam(requestedTeam.id);
    }
  }, [currentTeamId, requestedTeam, setCurrentTeam]);

  if (teamId && !requestedTeam) return <Navigate to="/" replace />;
  if (requestedTeam && currentTeamId !== requestedTeam.id) {
    return <div className="ui-screen text-sm text-gray-500">Team laden…</div>;
  }

  const activeTeam = teams.find(team => team.id === currentTeamId);
  if (!activeTeam) return <Navigate to="/" replace />;
  if (statistics) return canAccessTeamPlayerStatistics(currentUser, activeTeam)
    ? <PlayerStatistics key={activeTeam.id} /> : <Navigate to="/" replace />;
  return <TeamManagement />;
};

const SelectedMatchRoute = () => {
  const { t } = useI18n();
  const { matchId } = useParams<{ matchId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const setCurrentMatch = useMatchStore(state => state.setCurrentMatch);
  const currentMatch = useMatchStore(state => state.matches.find(match => match.id === state.currentMatchId) ?? null);
  const currentTeamId = useUserStore(state => state.currentTeamId);
  const setCurrentTeam = useUserStore(state => state.setCurrentTeam);
  const [isSelectingMatch, setIsSelectingMatch] = useState(true);
  const [matchSelectionFailed, setMatchSelectionFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!matchId) {
      setMatchSelectionFailed(true);
      setIsSelectingMatch(false);
      return () => { cancelled = true; };
    }
    setIsSelectingMatch(true);
    setMatchSelectionFailed(false);
    void selectMatchContext(matchId, {
      selectMatch: setCurrentMatch,
      readMatch: () => useMatchStore.getState().getCurrentMatch(),
      readTeamId: () => useUserStore.getState().currentTeamId,
      hasTeam: id => useUserStore.getState().teams.some(team => team.id === id),
      selectTeam: setCurrentTeam,
      cancelled: () => cancelled,
    })
      .then(() => {
        if (!cancelled) setIsSelectingMatch(false);
      })
      .catch(() => {
        if (!cancelled) {
          setMatchSelectionFailed(true);
          setIsSelectingMatch(false);
        }
      });
    return () => { cancelled = true; };
  }, [matchId, currentTeamId, setCurrentMatch, setCurrentTeam]);

  useEffect(() => {
    if (isSelectingMatch || matchSelectionFailed || !currentMatch) return;
    const nextPath = getSelectedMatchDestination(
      currentMatch.status,
      new URLSearchParams(location.search).get('prepare') === '1',
    );
    if (nextPath) {
      const nextSearch = new URLSearchParams(location.search);
      nextSearch.delete('prepare');
      navigate({
        pathname: nextPath,
        search: nextSearch.toString() ? `?${nextSearch.toString()}` : '',
        hash: location.hash,
      }, { replace: true, state: location.state });
    }
  }, [currentMatch, isSelectingMatch, location.hash, location.search, location.state, matchSelectionFailed, navigate]);

  if (matchSelectionFailed) return <Navigate to="/match" replace />;
  if (isSelectingMatch || !currentMatch || currentMatch.id !== matchId) {
    return <div className="ui-screen text-sm text-gray-500">{t('match_route_loading')}</div>;
  }
  if (getSelectedMatchDestination(
    currentMatch.status,
    new URLSearchParams(location.search).get('prepare') === '1',
  )) return null;
  return <MatchDashboard />;
};

// Keep this route's component identity stable when connection status changes,
// so an open club form is not remounted during a restart.
const ClubDetailWrapper = () => {
  const { id } = useParams<{ id: string }>();
  const user = useUserStore(state => state.currentUser);
  const allowed = canAccessClubDetail(user, id);
  useEffect(() => {
    if (allowed && id) useClubDetailStore.getState().setSelectedClubId(id);
  }, [id, allowed]);
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const destination = query.get('action') === 'add-player' ? '/players' : '/club-users';
  return allowed ? <Navigate to={`${destination}${location.search}`} replace /> : <Navigate to="/" replace />;
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const mainContentRef = useRef<HTMLElement | null>(null);
  // const location = useLocation(); // Not used currently
  const { currentUser, currentTeamId, isAdmin, isClubManager, isInitialized, getCurrentTeam, setLanguage, teams, setCurrentTeam: setCurrentTeamStore, logout } = useUserStore();
  const { getCurrentMatch, setCurrentMatch } = useMatchStore();
  const [apiConnectionStatus, setApiConnectionStatus] = useState<boolean | null>(null);
  const [drillAutosaveInfo, setDrillAutosaveInfo] = useState<{ status: 'idle' | 'saving' | 'saved' | 'error'; text: string } | null>(null);
  const [planAutosaveInfo, setPlanAutosaveInfo] = useState<{ status: 'idle' | 'saving' | 'saved' | 'error'; text: string } | null>(null);
  const [trainingSessionAutosaveInfo, setTrainingSessionAutosaveInfo] = useState<{ status: 'idle' | 'saving' | 'saved' | 'error'; text: string } | null>(null);
  const [lineupHasUnsavedChanges, setLineupHasUnsavedChanges] = useState(false);
  const { t, locale, setLocale } = useI18n();
  const showIssueForm = useIssueFormStore(state => state.isOpen);
  const closeIssueForm = useIssueFormStore(state => state.close);
  const openIssueForm = useIssueFormStore(state => state.open);
  const highPriorityIssues = useHighPriorityIssueStore(state => state.issues)
    .filter(isOpenHighPriorityIssue);
  const openHighPriorityModal = useHighPriorityIssueStore(state => state.open);
  const isDevEnv = isDevelopmentEnv();
  const currentTeam = getCurrentTeam();
  const isTrainer = Boolean(currentUser?.istrainer);
  const canAccessTrainingExercises = isTrainer || isClubManager();
  const canAccessTrainingContent = isTrainer;
  const canAccessCoachMatchTools = Boolean(currentUser?.iscoach) || Boolean(
    currentUser?.id &&
    currentTeam?.coach?.some(coach => coach.id === currentUser.id)
  );
  const hasManagementRole = isAdmin() || isClubManager();
  const mobileWorkspace = resolveMobileWorkspace({
    pathname: location.pathname,
    isTrainer,
    canAccessCoachTools: canAccessCoachMatchTools,
    hasManagementRole,
  });
  const canAccessPlayerStatistics = canAccessTeamPlayerStatistics(currentUser, currentTeam);
  const canAccessClubManagement = canAccessOwnClubManagement(currentUser);
  const setPortalPresence = useLivePresenceStore(state => state.setPortalPresence);
  const setPortalConnected = useLivePresenceStore(state => state.setPortalConnected);
  const resetPortalPresence = useLivePresenceStore(state => state.resetPortalPresence);
  const handlePortalStreamEvent = useCallback((event: StreamEvent) => {
    if (event.name === 'roadmap.updated') {
      window.dispatchEvent(new CustomEvent('roadmap-cache-updated', { detail: event.payload }));
    }
  }, []);

  const currentMatch = getCurrentMatch();

  const coachTeams = useMemo(
    () => teams.filter(team => team.coach.some(coach => coach.id === currentUser?.id)),
    [teams, currentUser?.id]
  );
  const hasCoachTeams = coachTeams.length > 0;
  const [accessResult, setAccessResult] = useState<RegistrationAccessResult | null>(null);
  const [accessRefreshKey, setAccessRefreshKey] = useState(0);
  const waitingState = registrationWaitingState(currentUser, accessResult);
  const accessStatus = currentUser?.id === accessResult?.userId && !currentUser?.club_id ? accessResult?.status : null;
  useEffect(() => {
    let cancelled = false;
    const userId = currentUser?.id;
    if (!isInitialized || !userId || currentUser?.club_id) return;
    const refresh = () => {
      getRegistrationAccessStatus().then(status => {
        if (!cancelled) setAccessResult({ userId, status });
      }).catch(() => {
        if (!cancelled) setAccessResult(previous => ({ userId, status: previous?.userId === userId ? previous.status : null, error: true }));
      });
    };
    refresh();
    window.addEventListener('focus', refresh);
    return () => { cancelled = true; window.removeEventListener('focus', refresh); };
  }, [currentUser?.id, currentUser?.club_id, isInitialized, accessRefreshKey]);

  const { userCount: portalUserCount, isConnected: portalConnected, isStopping: portalStopping } = useChannelStream({
    channel: 'portal',
    monitorConnection: true,
    enabled: Boolean(isInitialized && currentUser),
    notify: false,
    suppressConnectedToast: true,
    suppressPresenceToast: true,
    channelLabel: 'portal',
    onEvent: handlePortalStreamEvent,
  });

  useEffect(() => {
    setPortalConnected(portalConnected);
  }, [portalConnected, setPortalConnected]);

  useEffect(() => {
    if (!currentUser?.id || !portalConnected) return;
    return startPortalActivityTracking();
  }, [currentUser?.id, portalConnected]);

  useEffect(() => {
    if (!currentUser) {
      resetPortalPresence();
      return;
    }
    setPortalPresence(typeof portalUserCount === 'number' ? portalUserCount : null);
  }, [currentUser, portalUserCount, resetPortalPresence, setPortalPresence]);

  useEffect(() => {
    const onDrillAutosave = (event: Event) => {
      const customEvent = event as CustomEvent<{ status: 'idle' | 'saving' | 'saved' | 'error'; text: string } | null>;
      setDrillAutosaveInfo(customEvent.detail ?? null);
    };

    window.addEventListener('drill-editor-autosave', onDrillAutosave as EventListener);
    return () => {
      window.removeEventListener('drill-editor-autosave', onDrillAutosave as EventListener);
    };
  }, []);

  useEffect(() => {
    const onLineupUnsavedChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ hasUnsavedChanges?: boolean }>;
      setLineupHasUnsavedChanges(Boolean(customEvent.detail?.hasUnsavedChanges));
    };

    window.addEventListener('lineup-unsaved-change', onLineupUnsavedChange as EventListener);
    return () => {
      window.removeEventListener('lineup-unsaved-change', onLineupUnsavedChange as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!location.pathname.includes('/match-lineup')) {
      setLineupHasUnsavedChanges(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!location.pathname.includes('/training-practices/editor')) {
      setDrillAutosaveInfo(null);
    }
    if (!location.pathname.includes('/plans/') || !location.pathname.includes('/edit')) {
      setPlanAutosaveInfo(null);
    }
    if (!location.pathname.includes('/training-sessions/') || !location.pathname.includes('/edit')) {
      setTrainingSessionAutosaveInfo(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    const onPlanAutosave = (event: Event) => {
      const customEvent = event as CustomEvent<{ status: 'idle' | 'saving' | 'saved' | 'error'; text: string } | null>;
      setPlanAutosaveInfo(customEvent.detail ?? null);
    };

    window.addEventListener('training-plan-autosave', onPlanAutosave as EventListener);
    return () => {
      window.removeEventListener('training-plan-autosave', onPlanAutosave as EventListener);
    };
  }, []);

  useEffect(() => {
    const onTrainingSessionAutosave = (event: Event) => {
      const customEvent = event as CustomEvent<{ status: 'idle' | 'saving' | 'saved' | 'error'; text: string } | null>;
      setTrainingSessionAutosaveInfo(customEvent.detail ?? null);
    };

    window.addEventListener('training-session-autosave', onTrainingSessionAutosave as EventListener);
    return () => {
      window.removeEventListener('training-session-autosave', onTrainingSessionAutosave as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!isInitialized || !currentTeamId || currentMatch) return;
    try {
      const storedMatchId = typeof sessionStorage !== 'undefined'
        ? sessionStorage.getItem('currentMatchId')
        : null;
      if (storedMatchId) {
        void setCurrentMatch(storedMatchId).catch(() => {
          if (useMatchStore.getState().currentMatchId === storedMatchId) {
            void setCurrentMatch(null);
          }
        });
      }
    } catch {
      // ignore storage failures
    }
  }, [isInitialized, currentTeamId, currentMatch, setCurrentMatch]);

  useEffect(() => {
    const mainElement = mainContentRef.current;
    if (!mainElement) return;

    mainElement.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  useEffect(() => {
    if (!currentUser || !isInitialized) return;
    if (location.pathname !== '/') return;
    if (isAdmin() && !hasCoachTeams) {
      navigate('/dashboard', { replace: true });
      return;
    }

    const trainerHome = getTrainerOnlyHome({
      isTrainer,
      canAccessCoachTools: canAccessCoachMatchTools,
      hasManagementRole,
    });
    if (trainerHome) navigate(trainerHome, { replace: true });
  }, [currentUser, isInitialized, location.pathname, isAdmin, hasCoachTeams, navigate, isTrainer, canAccessCoachMatchTools, hasManagementRole]);

  const headerTips = useMemo(() => {
    const tips: Array<{
      id: string;
      title: string;
      description: string;
      actionLabel?: string;
      onAction?: () => void;
    }> = [];

    // Filter teams where user is a coach
    const selectableTeams = coachTeams;

    // Calculate total players logic (simplified for global header context)
    // We'll skip the complex PlayersMap logic here for simplicity/performance in global header,
    // or we can reuse `team.players.length` as a proxy if needed.
    // For "no players" tip, checking if teams have 0 visible players is usually enough.
    const hasPlayers = selectableTeams.some(t => (t.players && t.players.length > 0) || (t.playerCount ?? 0) > 0);

    // Tip 1: No teams assigned
    if (selectableTeams.length === 0) {
      const canManageTeams = canAccessClubManagement;
      tips.push({
        id: 'no-teams',
        title: t(accessStatus?.pending ? accessStatus.decision === 'rejected' ? 'register_access_rejected_title' : 'register_access_pending_title' : 'team_overview_header_tip_no_teams_title'),
        description: t(accessStatus?.pending ? accessStatus.decision === 'rejected' ? 'register_access_rejected_description' : 'register_access_header_hint' : 'team_overview_header_tip_no_teams_description', { club: accessStatus?.club_name ?? '', team: accessStatus?.team_name ?? '' }),
        actionLabel: canManageTeams
          ? t('team_overview_header_tip_no_teams_action')
          : undefined,
        onAction: canManageTeams ? () => navigate('/teams') : undefined
      });
    }

    // Tip 2: Team exists but none selected
    if (selectableTeams.length > 0 && !currentTeamId) {
      tips.push({
        id: 'select-team',
        title: t('team_overview_header_tip_select_team_title'),
        description: t('team_overview_header_tip_select_team_description'),
      });
    }

    // Tip 3: Teams exist but no players
    if (selectableTeams.length > 0 && !hasPlayers) {
      const hasSingleTeam = selectableTeams.length === 1;
      tips.push({
        id: 'no-players',
        title: t('team_overview_header_tip_no_players_title'),
        description: t('team_overview_header_tip_no_players_description'),
        actionLabel: hasSingleTeam ? t('team_overview_header_tip_no_players_action') : undefined,
        onAction: hasSingleTeam
          ? () => {
            if (selectableTeams[0]) {
              setCurrentTeamStore(selectableTeams[0].id);
            }
            window.setTimeout(() => {
              navigate('/team-management', { state: { scrollToPlayers: true } });
            }, 0);
          }
          : undefined
      });
    }

    return tips;
  }, [accessStatus, canAccessClubManagement, coachTeams, currentTeamId, navigate, setCurrentTeamStore, t]);
  const primaryHeaderTip = headerTips.find(tip => tip.id === 'no-teams')
    ?? headerTips.find(tip => tip.id === 'select-team')
    ?? headerTips.find(tip => tip.id === 'no-players')
    ?? headerTips[0];



  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== AUTH_STORAGE_KEY && event.key !== AUTH_USER_STORAGE_KEY) {
        return;
      }
      if (event.newValue !== null) {
        return;
      }

      useUserStore.getState().clearAuthState();
      resetPortalPresence();
      window.location.replace('/login');
    };

    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [navigate, resetPortalPresence]);

  useEffect(() => {
    const handleInvalidSession = () => {
      useUserStore.getState().clearAuthState();
      resetPortalPresence();
      window.location.replace('/login');
    };

    window.addEventListener('auth-session-invalid', handleInvalidSession);
    return () => window.removeEventListener('auth-session-invalid', handleInvalidSession);
  }, [resetPortalPresence]);

  useEffect(() => {
    const checkApiConnection = async () => {
      try {
        const response = await getBackendVersionApi();
        setApiConnectionStatus(Boolean(response));
      } catch {
        setApiConnectionStatus(false);
      }
    };

    checkApiConnection();
  }, []);

  // Listen for navigation events from LineupManagement
  useEffect(() => {
    const handleNavigateToSubstitution = () => {
      navigate('/match-team-schedule');
    };

    window.addEventListener('navigateToSubstitution', handleNavigateToSubstitution);
    return () => {
      window.removeEventListener('navigateToSubstitution', handleNavigateToSubstitution);
    };
  }, [navigate]);

  // Allow pages to request navigation to a specific admin tab
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { tab?: string } | undefined;
      if (detail?.tab) {
        // Map old tab names to routes
        const tabToRoute: Record<string, string> = {
          'dashboard': '/dashboard',
          'live': '/live',
          'clubs': '/clubs',
          'teams': '/teams',
          'players': '/players',
          'users': '/admin-users',
          'formations': '/admin-formations',
          'evaluations': '/admin-evaluations',
          'matchtypes': '/admin-matchtypes',
          'security-codes': '/admin-security-codes',
          'planning': '/match-planning',
          'lineup': '/match-lineup',
          'schedule': '/match-team-schedule',
          'player-schedule': '/match-player-schedule',
          'analysis': '/match-analysis',
          'team': '/team-management'
        };
        const route = tabToRoute[detail.tab] || `/${detail.tab}`;
        navigate(route);
      }
    };
    window.addEventListener('navigateToAdminTab', handler as EventListener);
    return () => {
      window.removeEventListener('navigateToAdminTab', handler as EventListener);
    };
  }, [navigate]);

  // Default redirect logic for root path - REMOVED to allow Hub at /
  // useEffect(() => {
  //   if (currentUser && isInitialized && location.pathname === '/') {
  //     // Logic removed to allow landing on Hub
  //   }
  // }, [currentUser, isInitialized, isAdmin, userTeams.length, location.pathname, navigate]);
  useEffect(() => {
    if (currentUser && currentUser.language && currentUser.language !== locale) {
      setLocale(currentUser.language as Locale, { updateUrl: !isExerciseAdministrationPath(location.pathname) });
    }
  }, [currentUser?.language]);





  /**
   * Handles the user logout process.
   * It calls the `logout` action from the user store and catches potential errors.
   * Regardless of success or failure, the user will typically be redirected to the login page
   * due to application state changes.
   */
  const handleLogout = async () => {
    try {
      await logout();
      resetPortalPresence();
      window.location.replace('/login');
    } catch (error) {
      console.error('Logout failed', error);
    }
  };


  const issueFormModal = showIssueForm ? (
    <div className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] top-14 z-30 min-[900px]:inset-0 min-[900px]:z-50">
      <IssueForm onClose={closeIssueForm} isOpen={showIssueForm} />
    </div>
  ) : null;

  const issueFloatingButton = currentUser ? (
    <button
      type="button"
      onClick={() => openIssueForm()}
      className="fixed bottom-8 right-8 z-40 hidden items-center gap-2 rounded-full bg-primary-600 px-4 py-2 text-white shadow-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-400 min-[900px]:flex"
    >
      <AppIcons.support size={18} />
      <span className="text-sm font-semibold">{t('issue_form_button')}</span>
    </button>
  ) : null;

  const connectionIssues = [
    apiConnectionStatus === false
      ? {
        key: 'api',
        title: 'API Connection Error',
        message: 'Could not reach the API. Please verify the API URL and key.'
      }
      : null
  ].filter(Boolean) as { key: string; title: string; message: string }[];

  if (currentUser && location.pathname !== '/account/delete' && !isPersonalExerciseWorkspacePath(location.pathname) && waitingState !== 'workspace') {
    return <RegistrationWaitingPage status={accessStatus?.pending ? accessStatus : null}
      error={apiConnectionStatus === false || waitingState === 'error'}
      onRetry={() => { setAccessResult(null); setAccessRefreshKey(key => key + 1); }}
      onLogout={() => { void handleLogout(); }} />;
  }

  if (connectionIssues.length > 0) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        <div className="flex flex-grow items-start justify-center px-4 pt-5 min-[900px]:items-center min-[900px]:px-0 min-[900px]:pt-0">
          <div className="w-full max-w-md space-y-2 min-[900px]:w-[36rem] min-[900px]:max-w-none min-[900px]:space-y-4">
            {connectionIssues.map(issue => (
              <div key={issue.key} role="alert" className="border-l-2 border-error-400 bg-white px-4 py-3 text-left shadow-sm min-[900px]:rounded-lg min-[900px]:border-l-0 min-[900px]:p-8 min-[900px]:text-center min-[900px]:shadow-md">
                <h1 className="mb-1 text-sm font-semibold text-error-600 min-[900px]:mb-4 min-[900px]:text-2xl min-[900px]:font-bold">{issue.title}</h1>
                <p className="text-xs leading-5 text-gray-600 min-[900px]:text-base">{issue.message}</p>
              </div>
            ))}
          </div>
        </div>
        {issueFloatingButton}
        {issueFormModal}
      </div>
    );
  }

  if (!isInitialized && /^\/(?:(?:nl|en)\/)?teams(?:\/|$)/.test(location.pathname)) {
    return <div role="status" className="p-6">{t('team_editor_loading')}</div>;
  }

  if (location.pathname === '/account/delete') return <AccountDeletion />;

  if (!currentUser) {
    return (
      <>
        <Routes>
          <Route path="*" element={<Login />} />
        </Routes>
        {issueFloatingButton}
        {issueFormModal}
      </>
    );
  }

  // Initial Hub page for users with no team selected
  // This is now handled via routing: if no team selected, they can still navigate, 
  // but if they hit a route requiring a team, they might need a redirect or the page handles it.
  // For now, we'll let routing handle it, but we might want a specific route for "No Team Selected".

  return (
    <div className="h-screen bg-gray-100 flex flex-col overflow-hidden">
      {issueFormModal}
      <HighPriorityIssueModal />


      {/* Layout check for authenticated routes */}
      <Routes>
        {/* Workspace Routes: With NavBar and Sidebar */}
        <Route element={
          <div className="flex min-h-0 flex-1 flex-col bg-gray-100 overflow-hidden">
            <div className="flex flex-1 min-h-0">
              <div className="hidden min-[900px]:block">
                <Sidebar />
              </div>
              {/* Main Content Area */}
              <div className="flex-1 flex flex-col relative min-h-0 min-w-0 z-0">
                {/* Top header area */}
                {connectionIssues.length > 0 && (
                  <div className="bg-error-50 p-2 text-center text-error-700 text-sm">
                    Connection Issue: {connectionIssues[0].message}
                  </div>
                )}

                <header className="relative flex h-14 flex-shrink-0 items-center justify-between bg-white px-3 sm:px-6">
                  {/* One calm, contextual next step instead of stacked notification cards. */}
                  {primaryHeaderTip && !drillAutosaveInfo && !planAutosaveInfo && !trainingSessionAutosaveInfo && (
                    <div className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 min-[1180px]:block">
                      <button
                        type="button"
                        disabled={!primaryHeaderTip.onAction}
                        onClick={primaryHeaderTip.onAction}
                        className="group flex max-w-[min(52vw,760px)] items-center gap-2 rounded-md border-l-2 border-amber-400 px-3 py-1.5 text-left text-sm text-amber-800 transition-colors disabled:cursor-default enabled:hover:border-amber-500 enabled:hover:bg-amber-50 enabled:hover:text-amber-950 enabled:focus-visible:border-amber-500 enabled:focus-visible:bg-amber-50 enabled:focus-visible:text-amber-950 enabled:focus-visible:outline-none enabled:focus-visible:ring-2 enabled:focus-visible:ring-amber-300 enabled:focus-visible:ring-offset-2"
                      >
                        <Lightbulb size={15} className="shrink-0 text-amber-700" aria-hidden="true" />
                        <span className="shrink-0 font-semibold text-amber-950">{primaryHeaderTip.title}</span>
                        <span className="hidden truncate text-xs lg:inline">{primaryHeaderTip.description}</span>
                        {primaryHeaderTip.actionLabel && (
                          <span className="shrink-0 text-xs font-semibold text-amber-950 underline-offset-4 transition-colors group-hover:underline group-focus-visible:underline">
                            {primaryHeaderTip.actionLabel}
                          </span>
                        )}
                      </button>
                    </div>
                  )}
                  <div className="flex min-w-0 items-center">
                    <BrandLogo className="min-[900px]:hidden" />
                    {/* Match Context Display */}
                    {currentMatch && (
                      <span className="hidden rounded-lg border border-gray-200 bg-gray-100 px-3 py-1 text-sm font-medium text-gray-800 min-[900px]:inline">
                        {currentMatch.home_team} vs {currentMatch.away_team}
                      </span>
                    )}
                  </div>

                  <div className="flex min-w-0 items-center gap-2 sm:gap-4">


                    {highPriorityIssues.length > 0 && (
                      <button
                        type="button"
                        onClick={openHighPriorityModal}
                        className="hidden items-center gap-2 rounded-lg bg-error-50 px-3 py-1.5 text-sm font-semibold text-error-700 transition-colors hover:bg-error-100 min-[900px]:inline-flex"
                      >
                        <AlertTriangle size={16} />
                        <span>{t('high_priority_issue_badge')}</span>
                      </button>
                    )}

                    <div className="flex items-center gap-2">
                      {location.pathname.includes('/training-practices/editor') && drillAutosaveInfo && (
                        <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm">
                          {drillAutosaveInfo.status === 'saving' && <Loader2 size={15} className="text-slate-500 animate-spin" />}
                          {drillAutosaveInfo.status === 'error' && <AlertCircle size={15} className="text-red-600" />}
                          {(drillAutosaveInfo.status === 'saved' || drillAutosaveInfo.status === 'idle') && (
                            <Save size={15} className="text-emerald-600" />
                          )}
                          <span className={drillAutosaveInfo.status === 'error' ? 'text-red-600 font-medium' : 'text-slate-600'}>
                            {drillAutosaveInfo.text}
                          </span>
                        </div>
                      )}
                      {location.pathname.includes('/plans/') && location.pathname.includes('/edit') && planAutosaveInfo && (
                        <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm">
                          {planAutosaveInfo.status === 'saving' && <Loader2 size={15} className="text-slate-500 animate-spin" />}
                          {planAutosaveInfo.status === 'error' && <AlertCircle size={15} className="text-red-600" />}
                          {(planAutosaveInfo.status === 'saved' || planAutosaveInfo.status === 'idle') && (
                            <Save size={15} className="text-emerald-600" />
                          )}
                          <span className={planAutosaveInfo.status === 'error' ? 'text-red-600 font-medium' : 'text-slate-600'}>
                            {planAutosaveInfo.text}
                          </span>
                        </div>
                      )}
                      {location.pathname.includes('/training-sessions/') && location.pathname.includes('/edit') && trainingSessionAutosaveInfo && (
                        <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm">
                          {trainingSessionAutosaveInfo.status === 'saving' && <Loader2 size={15} className="text-slate-500 animate-spin" />}
                          {trainingSessionAutosaveInfo.status === 'error' && <AlertCircle size={15} className="text-red-600" />}
                          {(trainingSessionAutosaveInfo.status === 'saved' || trainingSessionAutosaveInfo.status === 'idle') && (
                            <Save size={15} className="text-emerald-600" />
                          )}
                          <span className={trainingSessionAutosaveInfo.status === 'error' ? 'text-red-600 font-medium' : 'text-slate-600'}>
                            {trainingSessionAutosaveInfo.text}
                          </span>
                        </div>
                      )}
                      {location.pathname.includes('/match-lineup') && lineupHasUnsavedChanges && (
                        <span
                          className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-warning-100 text-warning-700 min-[900px]:hidden"
                          title={t('lineup_unsaved')}
                          aria-label={t('lineup_unsaved')}
                          role="status"
                        >
                          <Save size={18} aria-hidden="true" />
                        </span>
                      )}
                      <select
                        value={locale}
                        onChange={e => {
                          setLocale(e.target.value as Locale);
                          setLanguage(e.target.value as Locale);
                        }}
                        className="text-sm border-gray-300 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      >
                        <option value="nl">🇳🇱 NL</option>
                        <option value="en">🇬🇧 EN</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => navigate('/profile')}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-orange-50 hover:text-orange-600"
                        title={t('user_profile_title')}
                        aria-label={t('user_profile_title')}
                      >
                        <Settings size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-primary-50 hover:text-primary-600"
                        title={t('logout')}
                        aria-label={t('logout')}
                      >
                        <LogOut size={16} />
                      </button>
                    </div>
                  </div>
                </header>

                <ConnectionStatusBanner key={currentUser.id} connected={portalConnected} stopping={portalStopping} locale={locale} />

                <main
                  ref={mainContentRef}
                  className="relative flex min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto bg-gray-50 p-3 pb-[calc(4rem+env(safe-area-inset-bottom))] sm:p-6 min-[900px]:pb-6"
                >
                  <div key={`${location.pathname}${location.search}`} className="flex min-h-0 flex-1 flex-col">
                    <RouteLoadBoundary key={location.pathname}>
                      <Suspense fallback={<div className="ui-screen flex min-h-[30vh] items-center justify-center text-sm font-medium text-gray-500"><Loader2 size={18} className="mr-2 animate-spin" /> {t('loading')}…</div>}>
                        <Outlet />
                      </Suspense>
                    </RouteLoadBoundary>
                    <div className="mt-auto pt-32">
                      <Footer />
                    </div>
                  </div>
                </main>
              </div>
            </div>
          </div>
        }>
          <Route path="/" element={<Hub />} />
          <Route path="/coach/dashboard/*" element={<LegacyMobileRouteRedirect to="/" />} />
          <Route path="/coach/profile/*" element={<LegacyMobileRouteRedirect to="/profile" />} />
          <Route path="/coach/teams/*" element={<LegacyMobileRouteRedirect to="/" />} />
          <Route path="/coach/team/:teamId/*" element={<LegacyCoachTeamRedirect />} />
          <Route path="/coach/matches/*" element={<LegacyMobileRouteRedirect to="/match" />} />
          <Route path="/coach/match/:matchId/prepare/*" element={<LegacyCoachMatchRedirect prepare />} />
          <Route path="/coach/match/:matchId/*" element={<LegacyCoachMatchRedirect />} />
          <Route path="/trainer" element={<LegacyMobileRouteRedirect to="/trainer-dugout" />} />
          <Route path="/trainer/exercises/*" element={<LegacyMobileRouteRedirect to="/training-practices" />} />
          <Route path="/trainer/execises/*" element={<LegacyMobileRouteRedirect to="/training-practices" />} />
          <Route path="/trainer/viewer" element={<LegacyTrainerViewerRedirect />} />
          <Route path="/trainer/plans" element={<LegacyMobileRouteRedirect to="/training-exercise-plans" />} />
          <Route path="/trainer/plans/:planId" element={<LegacyTrainerPlanRedirect />} />
          <Route path="/trainer/sessions/*" element={<LegacyMobileRouteRedirect to="/training-sessions" />} />
          <Route path="/admin/*" element={<LegacyMobileRouteRedirect to="/dashboard" />} />
          <Route path="/dashboard" element={isAdmin() ? <AdminDashboard /> : <Navigate to="/" />} />
          <Route path="/match" element={<MatchPlanning />} />
          <Route path="/match/live" element={<MatchDashboard />} />
          <Route path="/match/:matchId" element={<SelectedMatchRoute />} />
          <Route path="/team-management" element={<TeamManagementRoute />} />
          <Route path="/team-management/:teamId/statistics" element={<TeamManagementRoute statistics />} />
          <Route path="/team-management/:teamId" element={<TeamManagementRoute />} />
          <Route path="/team-intake" element={currentTeam ? <TeamIntake /> : <Navigate to="/" />} />
          <Route path="/match-lineup" element={<LineupManagement />} />
          <Route path="/match-planning" element={canAccessCoachMatchTools ? <MatchPlanning /> : <Navigate to="/" />} />
          <Route path="/match-team-schedule" element={canAccessCoachMatchTools ? <SubstitutionSchedule /> : <Navigate to="/" />} />
          <Route path="/team-evaluation" element={<PlayerEvaluations />} />
          <Route path="/team-evaluation-distributions" element={<TeamEvaluationDistributions />} />
          <Route path="/team-evaluation-distributions/new" element={<TeamEvaluationDistributionForm />} />
          <Route path="/team-evaluation-distributions/:id/edit" element={<TeamEvaluationDistributionForm />} />
          <Route path="/match-player-schedule" element={<PlayerSchedule />} />
          <Route path="/profile" element={<UserProfile />} />
          <Route path="/push" element={<PushPage />} />
          <Route path="/admin-push" element={isAdmin() ? <AdminPushRegistrations /> : <Navigate to="/" />} />
          <Route path="/membership-approval" element={(isAdmin() || isClubManager()) ? <MembershipApproval /> : <Navigate to="/" />} />
          <Route path="/player-stats" element={canAccessPlayerStatistics ? <PlayerStatistics /> : <Navigate to="/" />} />
          <Route path="/players" element={(isAdmin() || isClubManager()) ? <PlayersManagement /> : <Navigate to="/" />} />
          <Route path="/clubs" element={isAdmin() ? <ClubsManagement /> : <Navigate to="/club-management" />} />
          <Route path="/club/:id" element={(isAdmin() || isClubManager()) ? <ClubDetailWrapper /> : <Navigate to="/" />} />
          <Route path="/club-users" element={canAccessClubManagement ? <ClubUsers /> : <Navigate to="/" />} />
          <Route path="/club-details" element={canAccessClubManagement ? <ClubDetails /> : <Navigate to="/" />} />
          <Route path="/club-exercises" element={canAccessClubManagement ? <ExerciseLibraryEntry fallback={<TrainingExercises key="club" clubLibrary />} /> : <Navigate to="/" />} />
          <Route path="/club-management" element={canAccessClubManagement ? <ClubManagementOverview /> : <Navigate to="/" />} />
          <Route path="/nl/teams/*" element={<LocalizedTeamsRedirect />} />
          <Route path="/en/teams/*" element={<LocalizedTeamsRedirect />} />
          <Route path="/teams" element={!isInitialized ? <div role="status">{t('team_editor_loading')}</div> : canAccessClubManagement ? <TeamsManagement /> : <Navigate to="/" />} />
          <Route path="/teams/new" element={!isInitialized ? <div role="status">{t('team_editor_loading')}</div> : canAccessClubManagement ? <TeamsManagement key="create" editorMode="create" /> : <Navigate to="/" />} />
          <Route path="/teams/:teamId/edit" element={!isInitialized ? <div role="status">{t('team_editor_loading')}</div> : canAccessClubManagement ? <TeamsManagement key="edit" editorMode="edit" /> : <Navigate to="/" />} />
          <Route path="/live" element={isAdmin() ? <LiveMatches /> : <Navigate to="/" />} />
          <Route path="/match-analysis" element={<MatchAnalysis />} />
          <Route path="/training-calendar" element={isDevEnv && isTrainer ? <TrainingCalendarPage /> : <Navigate to="/" />} />
          <Route path="/trainer-dugout" element={isTrainer ? <TrainerDugout /> : <Navigate to="/" />} />
          <Route path="/training-practices" element={canAccessTrainingExercises ? <ExerciseLibraryEntry fallback={<TrainingExercises key="trainer" />} /> : <Navigate to="/" />} />
          <Route path="/training-practices/:id" element={canAccessTrainingExercises ? <TrainingExerciseDetail /> : <Navigate to="/" />} />
          <Route path="/training-practices/editor" element={currentUser && new URLSearchParams(location.search).has('libraryDraftId') ? <Navigate replace to={{ pathname: `/${locale}/exercise-editor`, search: location.search }} /> : canAccessTrainingExercises ? <DrillEditor /> : <Navigate to="/" />} />
          <Route path="/training-exercise-plans" element={canAccessTrainingContent ? <TrainingExercisePlans /> : <Navigate to="/" />} />
          <Route path="/training-sessions" element={canAccessTrainingContent ? <TrainingSessions /> : <Navigate to="/" />} />
          <Route path="/training-sessions/:id/edit" element={canAccessTrainingContent ? <TrainingSessionEditor /> : <Navigate to="/" />} />
          <Route path="/plans/:id/edit" element={canAccessTrainingContent ? <TrainingPlanEditor /> : <Navigate to="/" />} />
          <Route path="/admin-users" element={isAdmin() ? <UserManagement /> : <Navigate to="/" />} />
          <Route path="/admin-contacts" element={isAdmin() ? <ContactManagement /> : <Navigate to="/" />} />
          <Route path="/exercise-editor" element={<ExerciseLibraryEditor />} />
          <Route path="/:locale/exercise-editor" element={<ExerciseLibraryEditor />} />
          <Route path="/:locale/exercises" element={<ExerciseLibraryEntry />} />
          <Route path="/exercises" element={<ExerciseLibraryEntry />} />
          <Route path="/admin-exercise-library" element={<ExerciseLibraryEntry platform />} />
          <Route path="/:locale/admin-exercise-library" element={<ExerciseLibraryEntry platform />} />
          <Route path="/admin-exercises" element={isAdmin() ? <CuratedExerciseManagement /> : <Navigate to="/" />} />
          <Route path="/admin-formations/new" element={isAdmin() ? <FormationEditor key="new" /> : <Navigate to="/" />} />
          <Route path="/admin-formations/:id/edit" element={isAdmin() ? <FormationEditor /> : <Navigate to="/" />} />
          <Route path="/admin-formations" element={isAdmin() ? <FormationsManagement /> : <Navigate to="/" />} />
          <Route path="/admin-evaluations" element={isAdmin() ? <EvaluationsManagement /> : <Navigate to="/" />} />
          <Route path="/admin-matchtypes" element={isAdmin() ? <MatchTypesManagement /> : <Navigate to="/" />} />
          <Route path="/admin-seasons" element={isAdmin() ? <SeasonsManagement /> : <Navigate to="/" />} />
          <Route path="/admin-security-codes" element={isAdmin() ? <SecurityCodesManagement /> : <Navigate to="/" />} />
          <Route path="/admin-decrypt-value" element={isAdmin() && isDevEnv ? <AdminDecryptValue /> : <Navigate to="/" />} />
          <Route path="/admin-webdav" element={isAdmin() ? <AdminWebDav /> : <Navigate to="/" />} />
          <Route path="/admin-database" element={isAdmin() ? <AdminDatabase /> : <Navigate to="/" />} />
          <Route path="/admin-email" element={isAdmin() ? <AdminEmail /> : <Navigate to="/" />} />
          {/* Fallback for unknown routes */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>

      {issueFloatingButton}
      <MobileShellNavigation
        hasTeam={Boolean(currentTeam)}
        isFeedbackOpen={showIssueForm}
        onCloseFeedback={closeIssueForm}
        onOpenFeedback={openIssueForm}
        workspace={mobileWorkspace}
      />
      <StatisticsRefreshNotifications />
      <ToastContainer
        position="top-center"
        autoClose={3500}
        closeButton={false}
        hideProgressBar
        newestOnTop
        limit={2}
        toastClassName="portal-toast"
      />
    </div>
  );
}

export default App;
